if (!customElements.get('sticky-product-showcase')) {
  customElements.define(
    'sticky-product-showcase',
    class StickyProductShowcase extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.enableDesktop = this.dataset.enableDesktop === 'true';
        this.enableMobile = this.dataset.enableMobile === 'true';

        if (!this.checkVisibility()) {
          this.style.display = 'none';
          return;
        }

        this.bar = this.querySelector('[data-showcase-bar]');
        this.barImage = this.querySelector('[data-showcase-bar-image]');
        this.barTitle = this.querySelector('[data-showcase-bar-title]');
        this.ctaBtn = this.querySelector('[data-showcase-cta]');
        this.picker = this.querySelector('[data-showcase-picker]');
        this.modalOverlay = this.querySelector('[data-showcase-modal-overlay]');
        this.modalContent = this.querySelector('[data-showcase-modal-content]');
        this.modalClose = this.querySelector('[data-showcase-modal-close]');
        this.hiddenForm = this.querySelector('[data-showcase-form]');
        this.hiddenVariantId = this.querySelector('[data-showcase-variant-id]');

        this.productsData = [];
        this.currentProductIndex = 0;
        this.currentVariant = null;
        this.selectedOptions = [];

        this.loadProductsData();
        this.setupBarClick();
        this.setupCTA();
        this.setupPicker();
        this.setupModalClose();
      }

      checkVisibility() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        return (isMobile && this.enableMobile) || (!isMobile && this.enableDesktop);
      }

      loadProductsData() {
        const dataScripts = this.querySelectorAll('[data-showcase-product-data]');
        dataScripts.forEach((script) => {
          const idx = parseInt(script.dataset.showcaseProductData, 10);
          try {
            this.productsData[idx] = JSON.parse(script.textContent);
          } catch (e) {
            console.error('Showcase: failed to parse product data', e);
          }
        });
      }

      /* === Bar click → open picker === */
      setupBarClick() {
        if (!this.bar) return;
        this.bar.addEventListener('click', (e) => {
          e.preventDefault();
          if (!this.picker) return; // single product, no picker
          const isOpen = this.picker.classList.toggle('is-open');
          this.bar.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });

        // Outside click closes picker
        this.outsideClick = (e) => {
          if (this.picker && !this.picker.contains(e.target) && !this.bar.contains(e.target)) {
            this.picker.classList.remove('is-open');
            this.bar.setAttribute('aria-expanded', 'false');
          }
        };
        document.addEventListener('click', this.outsideClick);
      }

      /* === Picker item click === */
      setupPicker() {
        if (!this.picker) return;
        this.picker.querySelectorAll('[data-showcase-pick]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const idx = parseInt(btn.dataset.productIndex, 10);

            // Update picker selection
            this.picker.querySelectorAll('[data-showcase-pick]').forEach(b => b.removeAttribute('aria-pressed'));
            btn.setAttribute('aria-pressed', 'true');

            // Update bar
            this.currentProductIndex = idx;
            this.updateBar(idx);

            // Close picker
            this.picker.classList.remove('is-open');
            this.bar.setAttribute('aria-expanded', 'false');
          });
        });
      }

      updateBar(idx) {
        const product = this.productsData[idx];
        if (!product) return;
        if (this.barImage && product.bar_image) {
          this.barImage.src = product.bar_image;
          this.barImage.removeAttribute('srcset');
          this.barImage.alt = product.title;
        }
        if (this.barTitle && product.bar_title) {
          this.barTitle.textContent = product.bar_title;
        }
      }

      /* === CTA → open modal === */
      setupCTA() {
        if (!this.ctaBtn) return;
        this.ctaBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openModal(this.currentProductIndex);
        });
      }

      /* === Modal === */
      setupModalClose() {
        if (!this.modalClose) return;
        this.modalClose.addEventListener('click', () => this.closeModal());
        this.modalOverlay.addEventListener('click', (e) => {
          if (e.target === this.modalOverlay) this.closeModal();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.modalOverlay.classList.contains('is-open')) {
            this.closeModal();
          }
        });
      }

      openModal(productIndex) {
        const product = this.productsData[productIndex];
        if (!product) return;

        this.currentProductIndex = productIndex;
        this.currentVariant = null;
        this.selectedOptions = [];

        // Initialize with first available variant
        const firstAvail = product.variants.find(v => v.available) || product.variants[0];
        if (firstAvail) {
          this.selectedOptions = [...firstAvail.options];
          this.currentVariant = firstAvail;
        }

        this.renderModal(product);
        this.modalOverlay.classList.add('is-open');
        document.body.classList.add('showcase-modal-open');
      }

      closeModal() {
        this.modalOverlay.classList.remove('is-open');
        document.body.classList.remove('showcase-modal-open');
        this.modalContent.innerHTML = '';
      }

      renderModal(product) {
        // Rating stars
        let ratingStars = '';
        if (product.rating) {
          const ratingValue = product.rating.rating || product.rating;
          const scaleMax = product.rating.scale_max || 5;
          const floor = Math.floor(ratingValue);
          const decimal = ratingValue - floor;
          let decVal = 0;
          if (decimal >= 0.3 && decimal <= 0.7) decVal = 0.5;
          else if (decimal > 0.7) decVal = 1;

          ratingStars = `
            <div class="showcase-modal__rating">
              <span class="rating-star" style="--rating: ${floor}; --rating-max: ${scaleMax}; --rating-decimal: ${decVal};"></span>
              <span class="showcase-modal__rating-count">(${product.rating_count || 0})</span>
            </div>
          `;
        }

        // Variant selectors
        let variantHTML = '';
        if (product.options.length > 0 && product.options[0] !== 'Title') {
          variantHTML = product.options.map((optName, optIdx) => {
            const values = [...new Set(product.variants.map(v => v.options[optIdx]))];
            const buttons = values.map((val) => {
              const isSelected = this.selectedOptions[optIdx] === val;
              const hasAvail = product.variants.some(v =>
                v.available && v.options[optIdx] === val &&
                product.options.every((_, oi) => oi === optIdx || v.options[oi] === this.selectedOptions[oi])
              );
              return `
                <button
                  type="button"
                  class="showcase-modal__variant-btn${isSelected ? ' is-selected' : ''}"
                  data-modal-option-name="${optName}"
                  data-modal-option-value="${val}"
                  data-modal-option-index="${optIdx}"
                  ${!hasAvail ? 'disabled' : ''}
                >${val}</button>
              `;
            }).join('');

            return `
              <div class="showcase-modal__variant-group">
                <span class="showcase-modal__variant-label">${optName}</span>
                <div class="showcase-modal__variant-options">${buttons}</div>
              </div>
            `;
          }).join('');
        }

        // Gallery
        const images = product.images || [];
        let galleryHTML = '';
        if (images.length > 0) {
          galleryHTML = `
            <div class="showcase-modal__gallery">
              <div class="showcase-modal__main-image">
                <img src="${images[0].src}" alt="${images[0].alt || product.title}" data-modal-main-image>
              </div>
              ${images.length > 1 ? `
                <div class="showcase-modal__thumbnails">
                  ${images.map((img, i) => `
                    <button type="button" class="showcase-modal__thumb${i === 0 ? ' is-active' : ''}" data-modal-thumb-index="${i}">
                      <img src="${img.thumb}" alt="${img.alt || product.title}" loading="lazy">
                    </button>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          `;
        }

        const priceHTML = this.renderPrice(this.currentVariant);
        const descShort = product.description ? product.description.substring(0, 150) : '';
        const hasLongDesc = product.description_full && product.description_full.length > 150;

        this.modalContent.innerHTML = `
          ${galleryHTML}
          <div class="showcase-modal__details">
            <h3 class="showcase-modal__title">${product.title}</h3>
            ${ratingStars}
            <div class="showcase-modal__price" data-modal-price>${priceHTML}</div>
            ${variantHTML ? `<div class="showcase-modal__variants" data-modal-variants>${variantHTML}</div>` : ''}
            <div class="showcase-modal__desc">
              <p data-modal-desc-short>${descShort}${hasLongDesc ? '...' : ''}</p>
              ${hasLongDesc ? `<button type="button" class="showcase-modal__desc-toggle" data-modal-desc-toggle>Leggi di più</button>` : ''}
              <div class="showcase-modal__desc-full" data-modal-desc-full style="display:none;">${product.description_full || ''}</div>
            </div>
            <div class="showcase-modal__actions">
              <button type="button" class="showcase-modal__atc button" data-modal-atc ${!this.currentVariant || !this.currentVariant.available ? 'disabled' : ''}>
                ${this.currentVariant && this.currentVariant.available ? 'Aggiungi al carrello' : 'Esaurito'}
              </button>
              <a href="${product.url}" class="showcase-modal__view button button--secondary">Vai al prodotto</a>
            </div>
          </div>
        `;

        this.setupModalVariantButtons(product);
        this.setupModalGallery();
        this.setupDescToggle();
        this.setupATCInModal(product);
      }

      renderPrice(variant) {
        if (!variant) return '<span class="showcase-modal__price-unavailable">Non disponibile</span>';
        const fmt = (cents) => {
          if (window.theme?.moneyFormat) {
            return window.theme.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2).replace('.', ','));
          }
          return '€' + (cents / 100).toFixed(2).replace('.', ',');
        };
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (onSale) {
          return `
            <span class="showcase-modal__price-compare">${fmt(variant.compare_at_price)}</span>
            <span class="showcase-modal__price-sale">${fmt(variant.price)}</span>
          `;
        }
        return `<span class="showcase-modal__price-regular">${fmt(variant.price)}</span>`;
      }

      setupModalVariantButtons(product) {
        this.modalContent.querySelectorAll('[data-modal-option-value]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (btn.hasAttribute('disabled')) return;

            const optIdx = parseInt(btn.dataset.modalOptionIndex, 10);
            const optVal = btn.dataset.modalOptionValue;

            this.selectedOptions[optIdx] = optVal;

            const sibs = this.modalContent.querySelectorAll(`[data-modal-option-index="${optIdx}"]`);
            sibs.forEach(s => s.classList.remove('is-selected'));
            btn.classList.add('is-selected');

            this.updateModalDisabledStates(product);

            const key = this.selectedOptions.join('|');
            const variantMap = new Map(product.variants.map(v => [v.options.join('|'), v]));
            this.currentVariant = variantMap.get(key) || null;

            const priceEl = this.modalContent.querySelector('[data-modal-price]');
            if (priceEl) priceEl.innerHTML = this.renderPrice(this.currentVariant);

            const atcBtn = this.modalContent.querySelector('[data-modal-atc]');
            if (atcBtn) {
              if (this.currentVariant && this.currentVariant.available) {
                atcBtn.removeAttribute('disabled');
                atcBtn.textContent = 'Aggiungi al carrello';
              } else {
                atcBtn.setAttribute('disabled', 'disabled');
                atcBtn.textContent = this.currentVariant ? 'Esaurito' : 'Non disponibile';
              }
            }
          });
        });
      }

      updateModalDisabledStates(product) {
        product.options.forEach((optName, optIdx) => {
          const buttons = this.modalContent.querySelectorAll(`[data-modal-option-index="${optIdx}"]`);
          buttons.forEach((btn) => {
            const val = btn.dataset.modalOptionValue;
            const hasAvail = product.variants.some((v) => {
              if (!v.available) return false;
              if (v.options[optIdx] !== val) return false;
              return product.options.every((_, oi) => {
                if (oi === optIdx) return true;
                return v.options[oi] === this.selectedOptions[oi];
              });
            });
            if (hasAvail) btn.removeAttribute('disabled');
            else btn.setAttribute('disabled', 'disabled');
          });
        });
      }

      setupModalGallery() {
        const thumbs = this.modalContent.querySelectorAll('[data-modal-thumb-index]');
        const mainImg = this.modalContent.querySelector('[data-modal-main-image]');
        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', (e) => {
            e.preventDefault();
            const idx = parseInt(thumb.dataset.modalThumbIndex, 10);
            const product = this.productsData[this.currentProductIndex];
            if (product && product.images[idx] && mainImg) {
              mainImg.src = product.images[idx].src;
              mainImg.alt = product.images[idx].alt || product.title;
              thumbs.forEach(t => t.classList.remove('is-active'));
              thumb.classList.add('is-active');
            }
          });
        });
      }

      setupDescToggle() {
        const toggle = this.modalContent.querySelector('[data-modal-desc-toggle]');
        const short = this.modalContent.querySelector('[data-modal-desc-short]');
        const full = this.modalContent.querySelector('[data-modal-desc-full]');
        if (toggle && short && full) {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            if (full.style.display === 'none') {
              short.style.display = 'none';
              full.style.display = 'block';
              toggle.textContent = 'Riduci';
            } else {
              full.style.display = 'none';
              short.style.display = 'block';
              toggle.textContent = 'Leggi di più';
            }
          });
        }
      }

      setupATCInModal(product) {
        const atcBtn = this.modalContent.querySelector('[data-modal-atc]');
        if (!atcBtn) return;
        atcBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (atcBtn.hasAttribute('disabled')) return;
          if (!this.currentVariant) return;

          atcBtn.classList.add('is-loading');
          atcBtn.setAttribute('aria-disabled', 'true');

          this.hiddenVariantId.value = this.currentVariant.id;
          const fd = new FormData(this.hiddenForm);
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          if (cart) {
            fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
            fd.append('sections_url', window.location.pathname);
            cart.setActiveElement(document.activeElement);
          }

          const cfg = fetchConfig('javascript');
          cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete cfg.headers['Content-Type'];
          cfg.body = fd;

          fetch((window.routes?.cart_add_url) || '/cart/add', cfg)
            .then((r) => r.json())
            .then((r) => {
              if (r.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-showcase',
                  productVariantId: fd.get('id'),
                  errors: r.errors || r.description,
                  message: r.message,
                });
                return;
              }
              if (!cart) { window.location = (window.routes?.cart_url) || '/cart'; return; }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-showcase',
                productVariantId: fd.get('id'),
                cartData: r,
              });
              cart.renderContents(r);
              this.closeModal();
            })
            .catch((err) => console.error('Showcase ATC error:', err))
            .finally(() => {
              atcBtn.classList.remove('is-loading');
              atcBtn.removeAttribute('aria-disabled');
            });
        });
      }
    }
  );
}